import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { CONVERT_DEFAULT_MINUTES, EVENT_DURATION_CHIPS } from '@goaltree/backend/convex/lib/events'
import { Calendar } from '@goaltree/ui/components/calendar'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import { enUS, es, fr, ja, pt, zhCN } from '@goaltree/ui/lib/locales'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import { CalendarDays } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { TimeField } from '@/components/time-field'
import { durationLabel, startOfLocalDay } from '@/lib/events'
import { optimisticCreateEvent } from '@/lib/events-optimistic'
import { nextHourStart } from '@/lib/time'
import { useEventSheet } from '@/store/event-sheet.store'

const DATE_FNS_LOCALES: Record<string, typeof enUS> = { en: enUS, es, fr, ja, pt, zh: zhCN }

// "Convert to event" asks when it happens: the task's due day (else today), the
// next hour, 30 min by default.
export function ConvertToEventDialog({
  taskId,
  title,
  dueDate,
  open,
  onOpenChange,
  onConverted,
}: {
  taskId: Id<'nodes'>
  // Lets the new event show in schedules before the server confirms.
  title?: string
  dueDate?: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConverted?: () => void
}) {
  const { t, i18n } = useTranslation()
  const convert = useMutation(api.events.convertTaskToEvent).withOptimisticUpdate(
    (store, { taskId: id, ...a }) => {
      if (title) optimisticCreateEvent(store, { ...a, title }, id)
    },
  )
  const openEvent = useEventSheet((s) => s.open)
  const [day, setDay] = useState(() => new Date(startOfLocalDay(dueDate ?? Date.now())))
  const [time, setTime] = useState(nextHourStart)
  const [allDay, setAllDay] = useState(false)
  const [minutes, setMinutes] = useState(CONVERT_DEFAULT_MINUTES)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (open) {
      setDay(new Date(startOfLocalDay(dueDate ?? Date.now())))
      setTime(nextHourStart())
    }
  }, [open, dueDate])

  async function submit() {
    setBusy(true)
    const [h, m] = time.split(':').map(Number)
    const base = startOfLocalDay(day.getTime())
    try {
      await convert({
        taskId,
        startsAt: allDay ? base : base + (h * 60 + m) * 60_000,
        ...(allDay ? { allDay: true } : { durationMinutes: minutes }),
      })
      onOpenChange(false)
      onConverted?.()
      toast.success(t('events.convertedToEvent'))
      openEvent(taskId)
    } catch {
      toast.error(t('events.convertFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-sm flex-col gap-3">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            {t('events.convertTitle')}
          </DialogTitle>
        </DialogHeader>
        <Calendar
          mode="single"
          selected={day}
          defaultMonth={day}
          onSelect={(d) => d && setDay(d)}
          locale={DATE_FNS_LOCALES[i18n.language.split('-')[0]] ?? enUS}
          classNames={{ root: 'w-full', today: 'rounded-(--cell-radius) bg-muted text-foreground' }}
        />
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span
            className={cn('flex items-center gap-2', allDay && 'pointer-events-none opacity-40')}
          >
            <TimeField
              value={time}
              onChange={setTime}
              ariaLabel={t('events.startTime')}
              className="rounded-md bg-muted/60 px-2 py-1 font-mono text-xs font-semibold"
            />
            <span
              role="radiogroup"
              aria-label={t('events.rowDuration')}
              className="flex rounded-lg bg-muted/60 p-0.5"
            >
              {EVENT_DURATION_CHIPS.map((d) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={minutes === d}
                  onClick={() => setMinutes(d)}
                  className={cn(
                    'cursor-pointer rounded-md px-2 py-1 font-mono text-[11.5px] font-semibold',
                    minutes === d ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
                  )}
                >
                  {durationLabel(d, t)}
                </button>
              ))}
            </span>
          </span>
          <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
            {t('events.allDay')}
          </label>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void submit()}
          className="h-10 cursor-pointer rounded-xl bg-primary text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {t('events.convertToEvent')}
        </button>
      </DialogContent>
    </Dialog>
  )
}
